import { useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LevelPill, control, errText, muted } from "./hzShared";

export const field = `${control} w-full font-normal`;
export function Field({ label, children, hint }) {
  return <label className="block text-[12px] font-bold">{label}{children}{hint && <span className={`mt-1 block text-[11px] font-normal ${muted}`}>{hint}</span>}</label>;
}
export function Pick({ value, onChange, options, placeholder, testId, allowNone }) {
  return <Select value={value || (allowNone ? "__none" : undefined)} onValueChange={v => onChange(v === "__none" ? "" : v)}>
    <SelectTrigger className={`${field} mt-1`} data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="max-h-72">{allowNone && <SelectItem value="__none">{allowNone}</SelectItem>}{options.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
  </Select>;
}

export function RiskPicker({ config, value, onChange, prefix }) {
  const level = value.l && value.c ? config.cells[value.l - 1][value.c - 1] : null;
  return <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
    <Field label="Likelihood"><Pick testId={`${prefix}-likelihood`} value={value.l ? String(value.l) : ""} onChange={v => onChange({ ...value, l: Number(v) })} placeholder="Choose" options={config.likelihood.map((l, i) => [i + 1, `${i + 1} · ${l}`])} /></Field>
    <Field label="Consequence"><Pick testId={`${prefix}-consequence`} value={value.c ? String(value.c) : ""} onChange={v => onChange({ ...value, c: Number(v) })} placeholder="Choose" options={config.consequence.map((c, i) => [i + 1, `${i + 1} · ${c}`])} /></Field>
    <div className="pb-2" data-testid={`${prefix}-preview`}>{level ? <LevelPill level={level} score={value.l * value.c} /> : <span className={`text-xs ${muted}`}>Score shows here</span>}</div>
    {level && <p className={`text-[11px] sm:col-span-3 ${level === "high" ? "font-bold text-[#c8102e]" : muted}`}>{config.method.responses[level]}</p>}
  </div>;
}

export function PhotoInput({ paths, onChange, testId }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); fd.append("category", "hazard"); const { data } = await api.post("/files/upload", fd); onChange([...paths, data.storage_path]); }
    catch (e) { toast.error(errText(e, "Upload failed")); } finally { setBusy(false); }
  };
  return <div className="mt-1 flex flex-wrap items-center gap-2">
    <label className={`${control} inline-flex cursor-pointer items-center gap-1.5`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Add photo
      <input type="file" accept="image/*" className="sr-only" data-testid={testId} onChange={e => upload(e.target.files?.[0])} /></label>
    {paths.length > 0 && <span className={`text-[12px] ${muted}`}>{paths.length} attached <button type="button" className="underline" onClick={() => onChange([])}>Clear</button></span>}
  </div>;
}

export function FormDialog({ open, onClose, title, description, children, onSubmit, busy, submitLabel = "Save", testId, wide }) {
  return <Dialog open={open} onOpenChange={v => !v && onClose()}>
    <DialogContent className={`max-h-[90vh] overflow-y-auto ${wide ? "sm:max-w-2xl" : "sm:max-w-lg"}`} data-testid={testId}>
      <DialogHeader><DialogTitle>{title}</DialogTitle>{description && <DialogDescription>{description}</DialogDescription>}</DialogHeader>
      <form className="space-y-3.5" onSubmit={e => { e.preventDefault(); onSubmit(); }}>
        {children}
        <DialogFooter className="gap-2 pt-2"><button type="button" onClick={onClose} className={control}>Cancel</button>
          <button type="submit" disabled={busy} data-testid={`${testId}-submit`} className="inline-flex h-11 items-center gap-2 rounded-[9px] bg-[#ff5f15] px-5 text-[13px] font-extrabold text-white hover:bg-[#e65100] disabled:opacity-50">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{submitLabel}</button></DialogFooter>
      </form>
    </DialogContent>
  </Dialog>;
}

export function useSubmit(onDone) {
  const [busy, setBusy] = useState(false);
  const run = async (fn, success) => {
    setBusy(true);
    try { const r = await fn(); if (success) toast.success(success); onDone?.(r?.data); }
    catch (e) { toast.error(errText(e)); if (e.response?.status === 409) onDone?.(null, true); }
    finally { setBusy(false); }
  };
  return [busy, run];
}
